import React from 'react';
import {
  Sparkles,
  Lightbulb,
  ThumbsUp,
  AlertTriangle,
  TrendingDown,
} from 'lucide-react';
import type { AIInsightsResult } from '@/services/PagesServices/CustomReportBuilderService';

interface InsightsViewProps {
  insights: AIInsightsResult | null;
  isLoading: boolean;
  hasReportConfig: boolean;
  onGenerate: () => void;
}

const InsightsView: React.FC<InsightsViewProps> = ({
  insights,
  isLoading,
  hasReportConfig,
  onGenerate,
}) => {
  if (isLoading) {
    return (
      <div className="crb-loading">
        <div className="crb-spinner" />
        <p>AI is analyzing your data...</p>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="crb-empty-state">
        <div className="crb-empty-icon"><Sparkles size={36} /></div>
        <h3>AI-Powered Insights</h3>
        <p>
          {hasReportConfig
            ? 'Click to let AI analyze your report data and generate actionable insights.'
            : 'Build a report first by selecting a data source and fields, then AI will analyze the data.'}
        </p>
        {hasReportConfig && (
          <button className="crb-btn primary" onClick={onGenerate} style={{ marginTop: '16px' }}>
            <Sparkles size={16} /> Generate Insights
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="crb-insights-container">
      {/* Summary */}
      <div className="crb-insight-card crb-insight-summary">
        <div className="crb-insight-card-icon" style={{ background: '#dbeafe', color: '#3b82f6' }}>
          <Sparkles size={20} />
        </div>
        <div>
          <h4>Executive Summary</h4>
          <p>{insights.summary}</p>
        </div>
      </div>

      {/* Highlights */}
      <div className="crb-insights-highlights">
        {insights.highlights.positive && (
          <HighlightCard type="positive" icon={<ThumbsUp size={18} />} text={insights.highlights.positive} />
        )}
        {insights.highlights.concern && (
          <HighlightCard type="concern" icon={<AlertTriangle size={18} />} text={insights.highlights.concern} />
        )}
        {insights.highlights.trend && (
          <HighlightCard type="trend" icon={<TrendingDown size={18} />} text={insights.highlights.trend} />
        )}
      </div>

      {/* Key Findings */}
      {insights.keyFindings.length > 0 && (
        <div className="crb-insight-section">
          <h4><Lightbulb size={16} /> Key Findings</h4>
          <div className="crb-insight-list">
            {insights.keyFindings.map((finding, i) => (
              <div key={i} className="crb-insight-list-item">
                <span className="crb-insight-number">{i + 1}</span>
                <span>{finding}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {insights.recommendations.length > 0 && (
        <div className="crb-insight-section">
          <h4><ThumbsUp size={16} /> Recommendations</h4>
          <div className="crb-insight-list">
            {insights.recommendations.map((rec, i) => (
              <div key={i} className="crb-insight-list-item rec">
                <span className="crb-insight-number rec">{i + 1}</span>
                <span>{rec}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <button className="crb-btn primary" onClick={onGenerate} style={{ marginTop: '16px' }}>
        <Sparkles size={16} /> Regenerate Insights
      </button>
    </div>
  );
};

const HighlightCard: React.FC<{
  type: 'positive' | 'concern' | 'trend';
  icon: React.ReactNode;
  text: string;
}> = ({ type, icon, text }) => (
  <div className={`crb-highlight-card ${type}`}>
    {icon}
    <div>
      <span className="crb-highlight-label">{type.charAt(0).toUpperCase() + type.slice(1)}</span>
      <p>{text}</p>
    </div>
  </div>
);

export default React.memo(InsightsView);
